import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Plus, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR } from '../lib/format';
import { computeDocument, MODES } from '../lib/pricing';
import AmPmTimePicker from '../components/AmPmTimePicker';

function newLine() { return { id: crypto.randomUUID(), name: '', sub: '', qty: 1, rate: 0, pricing_mode: 'standard', discount_value: 0, reason_code: '' }; }
function newGroup(name = 'Wedding') { return { id: crypto.randomUUID(), name, date: '', reporting_time: '', ready_by_time: '', venue: '', services: [newLine()], travel_lines: [] }; }

export default function QuotationBuilder() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const projId = search.get('project');
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [terms, setTerms] = useState([]);
  const [settings, setSettings] = useState(null);
  const [f, setF] = useState({
    client_name: '', project_id: projId || '', status: 'draft',
    event_groups: [newGroup()], travel_lines: [], package_discount: 0,
    complimentary: '', advance_pct: 30, notes: '',
  });

  useEffect(() => {
    Promise.all([
      api.get('/projects').then(r => setProjects(r.data)),
      api.get('/services').then(r => setServices(r.data.filter(x => x.active).sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))),
      api.get('/reason-codes').then(r => setReasons(r.data)),
      api.get('/terms').then(r => setTerms(r.data.sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))),
      api.get('/settings').then(r => setSettings(r.data)),
    ]);
    if (id) api.get(`/quotations/${id}`).then(r => {
      const d = r.data;
      if ((!d.event_groups || !d.event_groups.length) && d.events_snapshot?.length) {
        d.event_groups = d.events_snapshot.map(e => ({
          id: crypto.randomUUID(), name: e.name, date: e.date, reporting_time: '', ready_by_time: '', venue: '',
          services: (e.services||[]).map(s => ({ ...newLine(), name: s.name, qty: s.qty, rate: s.rate })),
          travel_lines: e.travel_lines || []
        }));
      }
      setF(d);
    });
  }, [id]);

  useEffect(() => {
    if (!id && settings && !f.notes) {
      setF(x => ({
        ...x,
        notes: settings.document_text?.quotation_note_default || 'Advance locks the date(s).',
        advance_pct: settings.default_advance_pct || 30,
      }));
    }
  }, [settings, id]);

  const comp = computeDocument(f.event_groups, f.travel_lines, f.package_discount);
  const total = comp.total;
  const advance = Math.round(total * (f.advance_pct||30) / 100);

  const updGroup = (gi, k, v) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], [k]: v }; setF({ ...f, event_groups: g }); };
  const updLine = (gi, li, k, v) => { const g = [...f.event_groups]; const s = [...g[gi].services]; s[li] = { ...s[li], [k]: v }; g[gi] = { ...g[gi], services: s }; setF({ ...f, event_groups: g }); };
  const addLine = (gi) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], services: [...(g[gi].services||[]), newLine()] }; setF({ ...f, event_groups: g }); };
  const rmLine = (gi, li) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], services: g[gi].services.filter((_,x) => x !== li) }; setF({ ...f, event_groups: g }); };
  const addGroup = () => setF({ ...f, event_groups: [...f.event_groups, newGroup('Reception')] });
  const rmGroup = (gi) => setF({ ...f, event_groups: f.event_groups.filter((_,x) => x !== gi) });

  const pickService = (gi, li, svcId) => {
    const svc = services.find(s => s.id === svcId);
    if (!svc) return;
    updLine(gi, li, 'name', svc.name);
    updLine(gi, li, 'sub', svc.description || '');
    updLine(gi, li, 'rate', svc.default_rate);
  };

  const save = async () => {
    const body = {
      ...f,
      subtotal: comp.subtotal, total, advance_amount: advance, savings: comp.valueGivenAway,
      value_given_away: comp.valueGivenAway,
      terms: terms.filter(t => t.on_quotation).map(t => ({ title: t.title, body: t.body })),
    };
    try {
      let r;
      if (id) r = await api.put(`/quotations/${id}`, body);
      else r = await api.post('/quotations', body);
      toast.success('Quotation saved');
      nav('/quotations');
    } catch { toast.error('Save failed'); }
  };

  const eventTypes = settings?.event_types || ['Wedding','Reception'];
  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-4xl mx-auto">
      <Link to="/quotations" className="eyebrow text-[var(--gold)]">← Quotations</Link>
      <h1 className="serif text-[28px] mt-2 mb-5">{id ? `Edit ${f.number || 'Quotation'}` : 'New Quotation'}</h1>

      <div className="card-cream p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Client name</label><input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} data-testid="qb-client"/></div>
          <div><label className="eyebrow">Project (optional)</label>
            <select value={f.project_id} onChange={e => setF({ ...f, project_id: e.target.value })} data-testid="qb-project">
              <option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Status</label>
            <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="qb-status">
              {['draft','sent','viewed','accepted','expired','lost'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><label className="eyebrow">Expires on</label><input type="date" value={f.expires_on || ''} onChange={e => setF({ ...f, expires_on: e.target.value })}/></div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div className="eyebrow">Events & Line Items</div>
        <button onClick={addGroup} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid="qb-add-event">+ Add Date/Event</button>
      </div>
      <div className="space-y-4 mt-3">
        {(f.event_groups||[]).map((g, gi) => (
          <div key={g.id} className="card-cream p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <select value={g.name} onChange={e => updGroup(gi,'name',e.target.value)} className="w-40">{eventTypes.map(t => <option key={t}>{t}</option>)}</select>
              <input type="date" value={g.date} onChange={e => updGroup(gi,'date',e.target.value)} className="w-40"/>
              {f.event_groups.length > 1 && <button onClick={() => rmGroup(gi)} className="text-[var(--overdue)] ml-auto"><Trash2 size={14}/></button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <AmPmTimePicker value={g.reporting_time} onChange={v => updGroup(gi,'reporting_time',v)} label="Reporting time" testId={`qb-grp-${gi}-report`}/>
              <AmPmTimePicker value={g.ready_by_time} onChange={v => updGroup(gi,'ready_by_time',v)} label="Ready by" testId={`qb-grp-${gi}-ready`}/>
              <div className="md:col-span-2"><label className="eyebrow">Venue</label><input value={g.venue} onChange={e => updGroup(gi,'venue',e.target.value)}/></div>
            </div>
            <div className="space-y-2">
              {(g.services||[]).map((line, li) => {
                const isComp = line.pricing_mode === 'complimentary';
                return (
                  <div key={line.id} className="border-l-2 pl-3" style={{borderColor: isComp?'var(--gold)':'var(--line)'}}>
                    <div className="grid grid-cols-[1fr_60px_100px_20px] gap-2 items-center mb-1">
                      <div>
                        <select value="" onChange={e => e.target.value && pickService(gi, li, e.target.value)} className="text-xs mb-1">
                          <option value="">— Pick from catalogue —</option>
                          {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.default_rate?'₹'+s.default_rate:'TBC'})</option>)}
                        </select>
                        <input placeholder="Service name" value={line.name} onChange={e => updLine(gi,li,'name',e.target.value)}/>
                      </div>
                      <input type="number" placeholder="Qty" value={line.qty} onChange={e => updLine(gi,li,'qty',parseInt(e.target.value||0))}/>
                      <input type="number" placeholder="Rate" value={line.rate} onChange={e => updLine(gi,li,'rate',parseFloat(e.target.value||0))} disabled={isComp}/>
                      <button onClick={() => rmLine(gi,li)} className="text-[var(--overdue)]"><X size={14}/></button>
                    </div>
                    <input placeholder="Sub description" value={line.sub||''} onChange={e => updLine(gi,li,'sub',e.target.value)} className="text-xs mb-1"/>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={line.pricing_mode} onChange={e => updLine(gi,li,'pricing_mode',e.target.value)} className="text-xs">
                        {MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                      </select>
                      {['flat','pct','per_head'].includes(line.pricing_mode) && (
                        <input type="number" placeholder={line.pricing_mode==='pct'?'% off':'₹ off'} value={line.discount_value||0} onChange={e => updLine(gi,li,'discount_value',parseFloat(e.target.value||0))} className="text-xs"/>
                      )}
                      {isComp && (
                        <select value={line.reason_code||''} onChange={e => updLine(gi,li,'reason_code',e.target.value)} className="text-xs">
                          <option value="">— Reason (required) —</option>
                          {reasons.map(r => <option key={r.id} value={r.code}>{r.label}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => addLine(gi)} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase mt-2">+ Line item</button>
          </div>
        ))}
      </div>

      <div className="card-cream p-4 mt-4">
        <div className="flex items-center justify-between mb-2"><div className="eyebrow">Travel & Logistics</div>
          <button onClick={() => setF({ ...f, travel_lines: [...(f.travel_lines||[]), { label:'', amount:0 }] })} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">+ Add</button>
        </div>
        {(f.travel_lines||[]).map((t, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_20px] gap-2 mb-2">
            <input placeholder="e.g. Outstation road, hotel, per-diem, early-call surcharge" value={t.label} onChange={e => { const tl=[...f.travel_lines]; tl[i]={...tl[i], label:e.target.value}; setF({...f, travel_lines: tl}); }}/>
            <input type="number" placeholder="₹" value={t.amount} onChange={e => { const tl=[...f.travel_lines]; tl[i]={...tl[i], amount:parseFloat(e.target.value||0)}; setF({...f, travel_lines: tl}); }}/>
            <button onClick={() => setF({...f, travel_lines: f.travel_lines.filter((_,x)=>x!==i)})} className="text-[var(--overdue)]"><X size={12}/></button>
          </div>
        ))}
      </div>

      <div className="card-cream p-4 mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="eyebrow">Package discount (₹)</label><input type="number" value={f.package_discount} onChange={e => setF({ ...f, package_discount: parseFloat(e.target.value||0) })} data-testid="qb-discount"/></div>
        <div><label className="eyebrow">Complimentary upgrade note</label><input placeholder="e.g. High-End upgrade for mother's makeup" value={f.complimentary} onChange={e => setF({ ...f, complimentary: e.target.value })}/></div>
        <div><label className="eyebrow">Advance %</label><input type="number" value={f.advance_pct} onChange={e => setF({ ...f, advance_pct: parseInt(e.target.value||0) })}/></div>
        <div><label className="eyebrow">Notes</label><input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}/></div>
      </div>

      <div className="card-alt p-5 mt-4">
        <div className="flex justify-between text-sm py-1"><span>Subtotal (list value)</span><span className="mono-num">{formatINR(comp.subtotal + comp.globalTravel)}</span></div>
        {f.package_discount > 0 && <div className="flex justify-between text-sm py-1" style={{color:'var(--gold)'}}><span>{settings?.document_text?.quotation_savings_label || 'You save'}</span><span className="mono-num">− {formatINR(f.package_discount)}</span></div>}
        <div className="flex justify-between text-lg py-2 font-medium border-t hairline mt-1"><span>Total</span><span className="serif mono-num">{formatINR(total)}</span></div>
        <div className="flex justify-between text-sm py-1" style={{color:'var(--ok)'}}><span>Advance ({f.advance_pct}%)</span><span className="mono-num">{formatINR(advance)}</span></div>
        {comp.valueGivenAway > 0 && <div className="flex justify-between text-xs pt-2 mt-2 border-t hairline text-[var(--muted)]"><span>{settings?.document_text?.value_given_line || 'Total value included at no charge'}</span><span className="mono-num">{formatINR(comp.valueGivenAway)}</span></div>}
      </div>

      <button className="btn-gold w-full mt-5" onClick={save} data-testid="qb-save">{id ? 'Save Quotation' : 'Create Quotation'}</button>
    </div>
  );
}
